import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Map as MapIcon, Navigation } from "lucide-react";
import { AdminTitle } from "@/admin/AdminLayout";
import { StatusBadge } from "@/components/Brand";
import { LOAN_STEPS, loanStage } from "@/components/LoanTimeline";
import { useApi } from "@/lib/api";
import { rp } from "@/lib/format";
import { mapsUrl } from "@/lib/geoPhoto";

const COLORS = { diajukan: "#64748b", verifikasi: "#2563eb", survei: "#f59e0b", keputusan: "#10b981", pencairan: "#dc2626" };
const FILTERS = [["proses", "Dalam Proses"], ["survei", "Perlu Survei"], ["all", "Semua"]];
const pin = (c) => L.divIcon({ className: "", iconSize: [26, 26], iconAnchor: [13, 26],
  html: `<svg width="26" height="26" viewBox="0 0 24 24"><path fill="${c}" stroke="#fff" stroke-width="1.5" d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7z"/><circle cx="12" cy="9" r="2.6" fill="#fff"/></svg>` });
const fmtWhen = (s) => new Date(s).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

export default function AdminLoanMap() {
  const navigate = useNavigate();
  const { data } = useApi("/admin/loans/map");
  const [filter, setFilter] = useState("proses");
  const box = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const list = (data || []).filter((l) => filter === "all" || (filter === "survei" ? l.stage === "survei" && l.status === "pending" : ["pending", "disetujui"].includes(l.status)));

  useEffect(() => {
    map.current = L.map(box.current, { center: [-0.03, 109.33], zoom: 11 });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "&copy; OpenStreetMap" }).addTo(map.current);
    layer.current = L.layerGroup().addTo(map.current);
    return () => map.current.remove();
  }, []);

  useEffect(() => {
    layer.current.clearLayers();
    list.forEach((l) => {
      const stage = loanStage(l);
      const html = `<b>${esc(l.member_name)}</b><br/>${rp(l.amount)} • ${LOAN_STEPS.find((s) => s.key === stage).label}` +
        (l.workplace ? `<br/>${esc(l.workplace)}` : "") + (l.schedule ? `<br/>Survei: ${fmtWhen(l.schedule.scheduled_at)} (${esc(l.schedule.officer)})` : "") +
        `<br/><a href="${mapsUrl(l.geo)}" target="_blank" rel="noreferrer">Rute Google Maps</a>`;
      L.marker([l.geo.lat, l.geo.lng], { icon: pin(COLORS[stage]) }).bindPopup(html).addTo(layer.current);
    });
    if (list.length) map.current.fitBounds(L.latLngBounds(list.map((l) => [l.geo.lat, l.geo.lng])).pad(0.3), { maxZoom: 15 });
  }, [data, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const focus = (l) => { map.current.setView([l.geo.lat, l.geo.lng], 16); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div>
      <AdminTitle icon={MapIcon} title="Peta Sebaran Pinjaman" sub="Lokasi rumah/tempat usaha peminjam untuk merencanakan rute survei" />
      <div className="flex flex-wrap gap-2 mb-3">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`loan-map-filter-${k}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border ${filter === k ? "bg-blue-800 text-white border-blue-800" : "bg-white border-slate-200 hover:bg-slate-50"}`}>{l}</button>
        ))}
        <div className="flex flex-wrap items-center gap-3 ml-auto text-[11px] text-slate-600">
          {LOAN_STEPS.map((s) => <span key={s.key} className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[s.key] }} />{s.label}</span>)}
        </div>
      </div>
      <div ref={box} className="h-[460px] rounded-2xl border border-slate-200 overflow-hidden z-0 relative" data-testid="loan-map" />
      <div className="mt-4 grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="loan-map-list">
        {list.length === 0 && <p className="text-sm text-slate-500" data-testid="loan-map-empty">Belum ada lokasi pinjaman untuk filter ini.</p>}
        {list.map((l) => (
          <div key={l.loan_id} className="bg-white rounded-xl border border-slate-200 p-3 text-sm" data-testid={`loan-map-item-${l.loan_id}`}>
            <div className="flex items-center justify-between gap-2"><b>{l.member_name}</b><StatusBadge status={l.status} /></div>
            <div className="text-xs text-slate-600">{rp(l.amount)} • {LOAN_STEPS.find((s) => s.key === loanStage(l)).label}</div>
            {l.schedule && <div className="text-xs text-amber-700 mt-0.5">Survei {fmtWhen(l.schedule.scheduled_at)} • {l.schedule.officer}</div>}
            <div className="flex gap-2 mt-2">
              <button onClick={() => focus(l)} data-testid={`loan-map-focus-${l.loan_id}`} className="text-xs font-semibold border border-slate-300 rounded-lg px-2 py-1">Lihat di peta</button>
              <a href={mapsUrl(l.geo)} target="_blank" rel="noreferrer" data-testid={`loan-map-route-${l.loan_id}`} className="text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 rounded-lg px-2 py-1 inline-flex items-center gap-1"><Navigation className="h-3 w-3" />Rute</a>
              <button onClick={() => navigate("/admin/pinjaman")} data-testid={`loan-map-open-${l.loan_id}`} className="text-xs font-semibold text-slate-700 underline">Detail</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
